import React, { useState, useEffect, useContext } from 'react';
import * as ScenarioAPI from '../../services/ScenarioServices';
import * as NotificationAPI from '../../services/NotificationServices';
import styles from './styles/ClassroomAssignmentsModal.module.css';
import { ClassroomContext } from '../../context/ClassroomContext';
import AuthContext from '../../context/AuthContext';
import Loading from '../loading/Loading';
import { toLocalDateKey } from '../../utils/dateUtils';
import { FaSave, FaTimes, FaSearch, FaCheckSquare, FaRegSquare, FaImage, FaUserCircle } from 'react-icons/fa';

const ClassroomAssignmentsModal = ({ onClose, assignment }) => {
    const { classroom, createAssignment, updateAssignment } = useContext(ClassroomContext);
    const { userId } = useContext(AuthContext);
    const isEditMode = Boolean(assignment);

    const [title, setTitle] = useState(assignment ? assignment.title : '');
    const [description, setDescription] = useState(assignment ? assignment.description : '');
    // Due dates are stored as local midnight, so read them back in local time — slicing the
    // ISO string gives the UTC date, a day early east of UTC, and saving would shift it.
    const [dueDate, setDueDate] = useState(
        assignment && assignment.dueDate ? toLocalDateKey(assignment.dueDate) : ''
    );
    const [selectedScenario, setSelectedScenario] = useState(assignment ? assignment.scenario._id : null);
    const [scenarios, setScenarios] = useState([]);
    const [scenarioSearch, setScenarioSearch] = useState('');
    const [assignAll, setAssignAll] = useState(assignment ? assignment.assignToAll : true);
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [disableToC, setDisableToC] = useState(assignment ? assignment.disableToC : false);
    const [disableRestart, setDisableRestart] = useState(assignment ? assignment.disableRestart : false);
    const [loading, setIsLoading] = useState(true);

    

    
    useEffect(() => {
        const fetchScenarios = async () => {
            if (userId) {
                try {
                    const data = await ScenarioAPI.getMyScenarios();
                    setScenarios(data);
                } catch (error) {
                    console.error('Error fetching teacher scenarios:', error);
                } finally {
                    setIsLoading(false);
                }
            } else {
                setIsLoading(false);
            }
        };
        fetchScenarios();
    }, [userId, classroom.students]);

    
    useEffect(() => {
        if (isEditMode && assignment && classroom && classroom.students) {
            if (assignment.assignToAll !== undefined) {
                setAssignAll(assignment.assignToAll);
                if (!assignment.assignToAll) {
                    setSelectedStudents(assignment.students.map(student => student._id || student.id));
                }
            } else {
                const isAll = assignment.students.length === classroom.students.length;
                setAssignAll(isAll);
                if (!isAll) {
                    setSelectedStudents(assignment.students.map(student => student._id || student.id));
                }
            }
        }
    }, [assignment, classroom, isEditMode]);

    const toggleStudentSelection = (studentId) => {
        if (selectedStudents.includes(studentId)) {
            setSelectedStudents(selectedStudents.filter(id => id !== studentId));
        } else {
            setSelectedStudents([...selectedStudents, studentId]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!title || !selectedScenario) {
            alert("Title and Scenario are required.");
            return;
        }

        setIsLoading(true);

        const assignmentData = {
            title,
            description,
            dueDate: dueDate ? new Date(`${dueDate}T00:00:00`) : null,
            scenario: selectedScenario,
            assignToAll: assignAll,
            students: assignAll
                ? classroom.students.map(student => student._id || student.id)
                : selectedStudents,
            disableToC,
            disableRestart,
        };

        try {
            if (isEditMode) {
                await updateAssignment(assignment._id, assignmentData);
            } else {
                const created = await createAssignment(classroom._id, assignmentData);

                // Notify whoever the server actually assigned, not who this page thinks is in
                // the classroom — its roster is a snapshot from page load, so anyone who joined
                // since would otherwise be assigned the work without ever being told.
                const assignees = created?.students ?? [];

                if (assignees.length > 0) {
                    await NotificationAPI.sendAssignmentToStudents(assignees, classroom._id, classroom.name, title);
                }
            }
            setIsLoading(false);
            onClose();
        } catch (error) {
            console.error('Error saving assignment:', error);
            setIsLoading(false);
        }
    };

    
    const filteredScenarios = scenarios.filter((scenario) =>
        scenario.title.toLowerCase().includes(scenarioSearch.toLowerCase())
    );

    if (loading) {
        return (
            <div className={styles.modalOverlay}>
                <div
                    className={styles.modalContent}
                    style={{ alignItems: 'center', justifyContent: 'center' }}
                >
                    <Loading />
                </div>
            </div>
        );
    }
    
    return (
        <div className={styles.modalOverlay}>
            <div
                className={styles.modalContent}
                role="dialog"
                aria-modal="true"
                aria-labelledby="assignmentModalTitle"
            >
                <h2 id="assignmentModalTitle" className={styles.modalTitle}>
                    {isEditMode ? 'Edit Assignment' : 'Create New Assignment'}
                </h2>
                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="assignmentTitle" className={styles.label}>
                            Title <span className={styles.requiredIndicator}>*</span>
                        </label>
                        <input
                            id="assignmentTitle"
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className={styles.inputField}
                            required
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <label htmlFor="assignmentDescription" className={styles.label}>
                            Description (Optional)
                        </label>
                        <textarea
                            id="assignmentDescription"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className={styles.textarea}
                            rows={3}
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <label htmlFor="assignmentDueDate" className={styles.label}>
                            Due Date (Optional)
                        </label>
                        <input
                            id="assignmentDueDate"
                            type="date"
                            value={dueDate}
                            onChange={(e) => setDueDate(e.target.value)}
                            className={styles.inputField}
                        />
                    </div>
                    <fieldset className={styles.fieldset}>
                        <legend className={styles.legend}>Assign To</legend>
                        <div className={styles.assignSection}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    className={styles.checkboxInput}
                                    checked={assignAll}
                                    onChange={(e) => setAssignAll(e.target.checked)}
                                />
                                <span className={styles.checkboxMark}>
                                    {assignAll ? <FaCheckSquare /> : <FaRegSquare />}
                                </span>
                                <span className={styles.checkboxText}>
                                    Assign to all students in classroom
                                </span>
                            </label>
                            {!assignAll && classroom?.students?.length > 0 && (
                                <div className={styles.studentsListContainer}>
                                    <p className={styles.subLabel}>
                                        Select individual students:
                                    </p>
                                    <div
                                        className={`${styles.listWrapper} ${styles.studentsListWrapper}`}
                                    >
                                        <ul className={styles.list}>
                                            {classroom.students.map((student) => (
                                                <li
                                                    key={student._id || student.id}
                                                    className={`${styles.listItem} ${
                                                        styles.studentItem
                                                    } ${
                                                        selectedStudents.includes(
                                                            student._id || student.id
                                                        )
                                                            ? styles.selected
                                                            : ''
                                                    }`}
                                                    onClick={() =>
                                                        toggleStudentSelection(
                                                            student._id || student.id
                                                        )
                                                    }
                                                    tabIndex={0}
                                                    role="checkbox"
                                                    aria-checked={selectedStudents.includes(
                                                        student._id || student.id
                                                    )}
                                                    onKeyPress={(e) => {
                                                        if (
                                                            e.key === 'Enter' ||
                                                            e.key === ' '
                                                        )
                                                            toggleStudentSelection(
                                                                student._id ||
                                                                    student.id
                                                            );
                                                    }}
                                                >
                                                    {student.profilePicture ? (
                                                        <img
                                                            src={student.profilePicture}
                                                            alt=""
                                                            className={
                                                                styles.studentAvatar
                                                            }
                                                        />
                                                    ) : (
                                                        <div
                                                            className={
                                                                styles.studentAvatarPlaceholder
                                                            }
                                                        >
                                                            <FaUserCircle />
                                                        </div>
                                                    )}
                                                    <span className={styles.listItemLabel}>
                                                        {student.username ||
                                                            'Unnamed Student'}
                                                    </span>
                                                    {selectedStudents.includes(
                                                        student._id || student.id
                                                    ) && (
                                                        <FaCheckSquare
                                                            className={styles.selectedIcon}
                                                        />
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            )}
                            {!assignAll &&
                                (!classroom?.students ||
                                    classroom.students.length === 0) && (
                                    <p
                                        className={styles.listItemEmpty}
                                        style={{ marginTop: '10px' }}
                                    >
                                        No students found in this classroom.
                                    </p>
                                )}
                        </div>
                    </fieldset>
                    <fieldset className={styles.fieldset}>
                        <legend className={styles.legend}>
                            Assignment Settings
                        </legend>
                        <p className={styles.tipText}>Toggle these off if the grades are important</p>
                        <div className={styles.inputGroup}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={disableToC}
                                    onChange={(e) => setDisableToC(e.target.checked)}
                                    className={styles.checkboxInput}
                                />
                                <span className={styles.checkboxMark}>
                                    {disableToC ? <FaCheckSquare /> : <FaRegSquare />}
                                </span>
                                <span className={styles.checkboxText}>
                                    Disable Table of Contents
                                </span>
                            </label>
                        </div>

                        <div className={styles.inputGroup}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={disableRestart}
                                    onChange={(e) => setDisableRestart(e.target.checked)}
                                    className={styles.checkboxInput}
                                />
                                <span className={styles.checkboxMark}>
                                    {disableRestart ? <FaCheckSquare /> : <FaRegSquare />}
                                </span>
                                <span className={styles.checkboxText}>
                                    Disable Restart Button
                                </span>
                            </label>
                        </div>
                    </fieldset>
                    <fieldset className={styles.fieldset}>
                        <legend className={styles.legend}>
                            Choose Assignment <span className={styles.requiredIndicator}>*</span>
                        </legend>
                        <div className={styles.searchWrapper}>
                            <FaSearch className={styles.searchIcon} />
                            <input
                                type="search"
                                placeholder="Search scenarios by title..."
                                value={scenarioSearch}
                                onChange={(e) => setScenarioSearch(e.target.value)}
                                className={`${styles.inputField} ${styles.searchInput}`}
                                aria-label="Search Scenarios"
                            />
                        </div>
                        <div
                            className={`${styles.listWrapper} ${styles.scenarioListWrapper}`}
                        >
                            <ul className={styles.list}>
                                {filteredScenarios.length === 0 && (
                                    <li className={styles.listItemEmpty}>
                                        No scenarios match your search.
                                    </li>
                                )}
                                {filteredScenarios.map((scenario) => (
                                    <li
                                        key={scenario._id}
                                        className={`${styles.listItem} ${styles.scenarioItem} ${
                                            selectedScenario === scenario._id
                                                ? styles.selected
                                                : ''
                                        }`}
                                        onClick={() => setSelectedScenario(scenario._id)}
                                        tabIndex={0}
                                        role="radio"
                                        aria-checked={selectedScenario === scenario._id}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ')
                                                setSelectedScenario(scenario._id);
                                        }}
                                    >
                                        {scenario.image ? (
                                            <img
                                                src={scenario.image}
                                                alt=""
                                                className={styles.scenarioImage}
                                            />
                                        ) : (
                                            <div
                                                className={styles.scenarioImagePlaceholder}
                                            >
                                                <FaImage />
                                            </div>
                                        )}
                                        <span className={styles.listItemLabel}>
                                            {scenario.title || 'Untitled Scenario'}
                                        </span>
                                        {selectedScenario === scenario._id && (
                                            <FaCheckSquare
                                                className={styles.selectedIcon}
                                            />
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </fieldset>
                </form>
                <footer className={styles.modalFooter}>
                    <div className={styles.buttonGroup}>
                        <button
                            type="button"
                            onClick={onClose}
                            className={`${styles.button} ${styles.buttonSecondary}`}
                        >
                            <FaTimes /> Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            className={`${styles.button} ${styles.buttonPrimary}`}
                            disabled={
                                !title?.trim() ||
                                !selectedScenario ||
                                (!assignAll &&
                                    selectedStudents.length === 0)
                            }
                        >
                            <FaSave />{' '}
                            {isEditMode
                                ? 'Update Assignment'
                                : 'Create Assignment'}
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    );
    
};

export default ClassroomAssignmentsModal;
